import Modal, { ModalProps } from "antd/lib/modal";
import React, { useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import "./SearchModal.scss";
import { Empty } from "antd";
import {
  Globe,
  Search,
  File,
  FileText,
  Files,
  House,
  Settings,
  UsersRound,
  BookText,
  Blocks,
  Download,
  Rocket,
  Palette,
  Crown,
  CreditCard,
  KeyRound,
  CircleUserRound,
  LayoutGrid,
  Bot,
} from "lucide-react";
import { connect } from "react-redux";
import { getActiveDomain } from "../../helpers/websites/getActiveDomain";
import DebouncedInput from "../DebouncedInput";
import fuzzysort from "fuzzysort";
import { checkIfPageIsDynamic } from "../../helpers/checkIfPageIsDynamic";
import {
  getCurrentStoreData,
  getSingleWebsiteLoadStatus,
} from "../../store/websites/thunks";
import { GetCurrentStoreData } from "../../store/websites/types";
import { changeWebsitesStateGlobalData } from "../../store/websites/actions";
import { setUserStateData } from "../../store/user/actions";
import { getWebsiteInitialPagesLoadStatus } from "../../store/websitePages/thunks";
import { formatImageUrl } from "../../helpers/strings/formatImageUrl";
import _ from "lodash";
import { getSingleBlogLoadStatus } from "../../store/blogs/thunks";
import { getInitialPostsStatus } from "../../store/blogPosts/thunks";
import { LoadStatus } from "../../enums/enums";
import { sortByDateFn } from "../../helpers/sortByDateFn";

interface Props extends ModalProps, RouteComponentProps {
  websites: any;
  websitesPages: any;
  blogPosts: any;
  blogs: any;
  user: any;
  changeWebsitesStateGlobalData: any;
  getCurrentStoreData: GetCurrentStoreData;
  setUserStateData: any;
  getWebsiteInitialPagesLoadStatus: any;

  setSidebarMenu: (menu: string) => void;
  setIsSettingsModalVisible: (visible: boolean) => void;

  getSingleBlogLoadStatus: any;
  getInitialPostsStatus: any;
  getSingleWebsiteLoadStatus: any;
}

interface ResultButtonProps {
  children?: React.ReactNode;
  onClick?: () => void;
  icon?: React.ReactNode;
  subtitle?: string;
}
const ResultButton = (props: ResultButtonProps) => {
  const { children, onClick, icon, subtitle } = props;
  return (
    <button
      className="search-modal__result-button"
      onClick={() => {
        if (onClick) {
          onClick();
        }
      }}
    >
      {icon && <div className="search-modal__result-button-icon">{icon}</div>}
      <span className="search-modal__result-button-text">
        <span
          style={{
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            maxWidth: "380px",
          }}
        >
          {children}
        </span>
      </span>
    </button>
  );
};

interface ResultsGroupProps {
  title?: string;
  children?: React.ReactNode;
}
const ResultsGroup = (props: ResultsGroupProps) => {
  const { title, children } = props;
  return (
    <div className="search-modal__results-group">
      {title && (
        <div className="search-modal__results-group-title">{title}</div>
      )}
      <div className="search-modal__results-group-content">{children}</div>
    </div>
  );
};

interface ResultsProps extends Props {
  inputValue: string;
  handleOnCancel: (e: any) => void;
}
const Results = (props: ResultsProps) => {
  const {
    websites,
    websitesPages,
    blogPosts,
    blogs,
    inputValue,
    handleOnCancel,
    changeWebsitesStateGlobalData,
  } = props;
  const { currentWebsite, currentBlog } = props.getCurrentStoreData();
  const websitesSearchPool = websites.items
    .sort(sortByDateFn)
    .map((website) => {
      const getIcon = (website) => {
        if (!website) return null;
        const faviconUrl = _.get(website, "favicon_16x16_url");
        if (faviconUrl) {
          return (
            <img
              src={formatImageUrl(faviconUrl)}
              style={{
                width: "16px",
                height: "16px",
                objectFit: "contain",
              }}
            />
          );
        }
        return <Globe size={16} />;
      };
      return {
        id: website.id,
        type: "website",
        query: `website ${getActiveDomain(website)}`,
        icon: getIcon(website),
        title: getActiveDomain(website),
        onClick: () => {
          handleOnCancel(null);
          props.history.push(`/${website.subdomain}`);
          window.scrollTo(0, 0);
          props.setSidebarMenu("website");
        },
      };
    });
  const websitesGroup = {
    type: "website",
    title: "Websites",
    largestScore: null,
    items: [],
    poolItems: websitesSearchPool.slice(0, 3),
    hidden: false,
    loading: false,
  };

  fuzzysort
    .go(inputValue, websitesSearchPool, {
      keys: ["query"],
    })
    .map((result) => {
      const { obj } = result as any;
      const searchPoolObj = websitesSearchPool.find(
        (website) => website.id === obj.id
      );
      return {
        ...searchPoolObj,
        score: result.score,
      };
    })
    .forEach((website) => {
      if (
        websitesGroup.largestScore === null ||
        website.score > websitesGroup.largestScore
      ) {
        websitesGroup.largestScore = website.score;
      }
      websitesGroup.items.push(website);
    });

  const pagesSearchPool = websitesPages.items
    .filter((page) => {
      return currentWebsite && page.website === currentWebsite.id;
    })
    .sort(sortByDateFn)
    .map((page) => {
      const isPageDynamic = checkIfPageIsDynamic(page);

      return {
        id: page.id,
        type: "page",
        query: `${page.url ? `/${page.url}` : "home"} page`,
        icon: isPageDynamic ? (
          <Files size={16} />
        ) : !page.url ? (
          <House size={16} />
        ) : (
          <File size={16} />
        ),
        title: page.url ? `/${page.url}` : "Home",
        onClick: () => {
          handleOnCancel(null);
          props.history.push(`/${currentWebsite.subdomain}/pages/${page.id}`);
          props.changeWebsitesStateGlobalData({
            cmsRerenderKey: props.websites.cmsRerenderKey + 1,
          });
          window.scrollTo(0, 0);
          props.setSidebarMenu("website");
        },
      };
    });
  const pagesGroup = {
    type: "page",
    title: "Pages",
    largestScore: null,
    items: [],
    poolItems: pagesSearchPool.slice(0, 3),
    hidden: !currentWebsite,
    loading:
      props.getWebsiteInitialPagesLoadStatus(currentWebsite) !==
        LoadStatus.LOADED || !props.blogs.dataFetched,
  };

  fuzzysort
    .go(inputValue, pagesSearchPool, {
      keys: ["query"],
    })
    .map((result) => {
      const { obj } = result as any;
      const searchPoolObj = pagesSearchPool.find((page) => page.id === obj.id);
      return {
        ...searchPoolObj,
        score: result.score,
      };
    })
    .forEach((page) => {
      if (
        pagesGroup.largestScore === null ||
        page.score > pagesGroup.largestScore
      ) {
        pagesGroup.largestScore = page.score;
      }
      pagesGroup.items.push(page);
    });

  const blogPostsSearchPool = blogPosts.items
    .filter((blogPost) => {
      const blog = blogs.items.find((blog) => blog.id === blogPost.blog);
      const websiteId = blog.website;
      return currentWebsite && websiteId === currentWebsite.id;
    })
    .sort(sortByDateFn)
    .map((blogPost) => {
      return {
        id: blogPost.id,
        type: "blogPost",
        query: `${
          blogPost.title ? blogPost.title : `/${blogPost.url} blog post`
        }`,
        icon: <FileText size={16} />,
        title: blogPost.title ? blogPost.title : `/${blogPost.url}`,
        onClick: () => {
          handleOnCancel(null);
          props.history.push(
            `/${currentWebsite.subdomain}/blog/${blogPost.id}`
          );
          window.scrollTo(0, 0);
          props.setSidebarMenu("blog");
        },
      };
    });
  const blogPostsGroup = {
    type: "blogPost",
    title: "Blog posts",
    largestScore: null,
    items: [],
    poolItems: blogPostsSearchPool.slice(0, 3),
    hidden: !currentWebsite,
    loading: props.getInitialPostsStatus(currentBlog) !== LoadStatus.LOADED,
  };

  fuzzysort
    .go(inputValue, blogPostsSearchPool, {
      keys: ["query"],
    })
    .map((result) => {
      const { obj } = result as any;
      const searchPoolObj = blogPostsSearchPool.find(
        (blogPost) => blogPost.id === obj.id
      );
      return {
        ...searchPoolObj,
        score: result.score,
      };
    })
    .forEach((blogPost) => {
      if (
        blogPostsGroup.largestScore === null ||
        blogPost.score > blogPostsGroup.largestScore
      ) {
        blogPostsGroup.largestScore = blogPost.score;
      }
      blogPostsGroup.items.push(blogPost);
    });
  const itemsKey = !!inputValue ? "items" : "poolItems";

  const handleOnClickSettings = (settings: string) => {
    handleOnCancel(null);
    props.setUserStateData({
      settings: settings,
    });
    props.setIsSettingsModalVisible(true);
  };

  const settingsSearchPool = [
    {
      id: -1,
      type: "settings",
      query: `theme colors fonts styles settings`,
      icon: <Palette size={16} />,
      title: "Theme",
      onClick: () => {
        handleOnCancel(null);
        props.setSidebarMenu("theme");
      },
    },
    {
      id: 0,
      type: "settings",
      query: `general settings`,
      icon: <Settings size={16} />,
      title: "General",
      onClick: () => {
        handleOnClickSettings("websiteSettings");
      },
    },
    {
      id: 1,
      type: "settings",
      query: `seo settings`,
      icon: <Bot size={16} />,
      title: "SEO",
      onClick: () => {
        handleOnClickSettings("seo");
      },
    },
    {
      id: 2,
      type: "settings",
      query: `collaborators settings`,
      icon: <UsersRound size={16} />,
      title: "Collaborators",
      onClick: () => {
        handleOnClickSettings("collaborators");
      },
    },
    {
      id: 3,
      type: "settings",
      query: `custom domain settings`,
      icon: <Globe size={16} />,
      title: "Custom domain",
      onClick: () => {
        handleOnClickSettings("customDomain");
      },
    },
    {
      id: 5,
      type: "settings",
      query: `widgets settings`,
      icon: <LayoutGrid size={16} />,
      title: "Widgets",
      onClick: () => {
        handleOnClickSettings("widgets");
      },
    },
    {
      id: 4,
      type: "settings",
      query: `blog settings`,
      icon: <BookText size={16} />,
      title: "Blog",
      onClick: () => {
        handleOnClickSettings("blogSettings");
      },
    },
    {
      id: 6,
      type: "settings",
      query: `export download html settings`,
      icon: <Download size={16} />,
      title: "Export HTML",
      onClick: () => {
        handleOnClickSettings("exportHtml");
      },
    },
    {
      id: 7,
      type: "settings",
      query: `upgrade plan settings`,
      icon: <Crown size={16} />,
      title: "Upgrade",
      onClick: () => {
        handleOnClickSettings("account__plan");
      },
    },
    {
      id: 8,
      type: "settings",
      query: `billing settings`,
      icon: <CreditCard size={16} />,
      title: "Billing",
      onClick: () => {
        handleOnClickSettings("account__billing");
      },
    },
    {
      id: 9,
      type: "settings",
      query: `account email password log out settings`,
      icon: <CircleUserRound size={16} />,
      title: "Account",
      onClick: () => {
        handleOnClickSettings("account");
      },
    },
    {
      id: 10,
      type: "settings",
      query: `connections zapier settings`,
      icon: <KeyRound size={16} />,
      title: "Connections",
      onClick: () => {
        handleOnClickSettings("account__applications");
      },
    },
  ];
  const settingsGroup = {
    type: "settings",
    title: "Settings",
    largestScore: null,
    items: [],
    poolItems: settingsSearchPool,
    hidden: !currentWebsite,
    loading:
      props.getSingleBlogLoadStatus(currentBlog) !== LoadStatus.LOADED ||
      props.getSingleWebsiteLoadStatus(currentWebsite) !== LoadStatus.LOADED ||
      !props.user.dataFetched ||
      !props.websites.dataFetched,
  };
  fuzzysort
    .go(inputValue, settingsSearchPool, {
      keys: ["query"],
    })
    .map((result) => {
      const { obj } = result as any;
      const searchPoolObj = settingsSearchPool.find(
        (setting) => setting.id === obj.id
      );
      return {
        ...searchPoolObj,
        score: result.score,
      };
    })
    .forEach((setting) => {
      if (
        settingsGroup.largestScore === null ||
        setting.score > settingsGroup.largestScore
      ) {
        settingsGroup.largestScore = setting.score;
      }
      settingsGroup.items.push(setting);
    });
  const displayedResults = [
    websitesGroup,
    pagesGroup,
    blogPostsGroup,
    settingsGroup,
  ]
    .filter((group) => {
      if (!inputValue) return true;
      return group.items.length > 0;
    })
    .sort((a, b) => {
      if (a.largestScore === null || b.largestScore === null) {
        return 0;
      }
      return b.largestScore - a.largestScore;
    })

    .map((group) => {
      if (group.hidden) return null;
      if (group.loading) return null;
      if (group.items.length === 0 && group.poolItems.length === 0) {
        return null;
      }
      return (
        <ResultsGroup key={group.type} title={group.title}>
          {group[itemsKey].map((item) => {
            return (
              <ResultButton
                key={`${item.id}-${item.type}`}
                icon={item.icon}
                onClick={item.onClick}
              >
                {item.title}
              </ResultButton>
            );
          })}
        </ResultsGroup>
      );
    });

  return (
    <div className="search-modal__results">
      {displayedResults.length > 0 ? (
        displayedResults
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No results" />
      )}
    </div>
  );
};

const SearchModal = (props: Props) => {
  const { visible, onCancel } = props;
  const [inputValue, setInputValue] = useState("");

  const handleOnCancel = (e: any) => {
    onCancel(e);
    setInputValue("");
  };

  return (
    <Modal
      className={`search-modal`}
      centered
      visible={visible}
      onCancel={handleOnCancel}
      footer={null}
      width={"100%"}
      zIndex={1031}
      destroyOnClose
      transitionName=""
    >
      <div className="search-modal__content">
        <div className="search-modal__input">
          <DebouncedInput
            prefix={<Search size={16} />}
            placeholder="Search..."
            autoFocus
            value={inputValue}
            onChange={(value) => setInputValue(value)}
            debounceDelay={250}
          />
        </div>
        <Results
          {...props}
          inputValue={inputValue}
          handleOnCancel={handleOnCancel}
        />
      </div>
    </Modal>
  );
};

const mapStateToProps = (state: any) => {
  return {
    websites: state.websites,
    websitesPages: state.websitesPages,
    blogPosts: state.blogPosts,
    blogs: state.blogs,
    user: state.user,
  };
};
export default connect(mapStateToProps, {
  getCurrentStoreData,
  changeWebsitesStateGlobalData,
  setUserStateData,

  getWebsiteInitialPagesLoadStatus,
  getSingleBlogLoadStatus,
  getInitialPostsStatus,
  getSingleWebsiteLoadStatus,
})(withRouter(SearchModal));
